import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { Navbar, PageHeader, Button } from '../components';
import LoadingSpinner from '../components/LoadingSpinner';

function formatDate(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'JUST NOW';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}M AGO`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}H AGO`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}D AGO`;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  }).toUpperCase();
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { profile, walletBalance } = useUser();
  const { logout } = useAuth();
  const { notifications, unreadCount, isLoading, fetchNotifications, markAsRead, markAllAsRead } = useNotifications();

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const loadNotifications = useCallback(async (pageNum) => {
    const result = await fetchNotifications(pageNum, 20);
    if (result) {
      setHasMore(result.hasMore);
      setTotalCount(result.totalCount);
    }
  }, [fetchNotifications]);

  useEffect(() => {
    loadNotifications(1);
  }, [loadNotifications]);

  const handleLoadMore = async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    await loadNotifications(nextPage);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar
        user={profile}
        walletBalance={walletBalance}
        onLogout={handleLogout}
        onLogoClick={() => navigate(profile?.initialMode === 'host' ? '/host' : '/guest')}
      />

      <PageHeader
        title="NOTIFICATIONS"
        subtitle={`${totalCount} total \u00b7 ${unreadCount} unread`}
        actions={
          unreadCount > 0 ? (
            <Button variant="secondary" size="sm" onClick={handleMarkAllRead}>
              MARK ALL READ
            </Button>
          ) : null
        }
      />

      <div className="max-w-3xl mx-auto">
        {isLoading && notifications.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <LoadingSpinner label="LOADING..." />
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-6 py-16 text-center border-b-2 border-black">
            <div className="font-mono text-lg font-bold text-gray-400 mb-2">NO NOTIFICATIONS</div>
            <div className="font-mono text-sm text-gray-400">
              When something happens, you'll see it here.
            </div>
          </div>
        ) : (
          <div className="divide-y-2 divide-black border-b-2 border-black">
            {notifications.map((notification) => (
              <button
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`w-full text-left px-6 py-4 hover:bg-gray-50 transition-colors ${
                  !notification.isRead ? 'bg-gray-50' : ''
                }`}
              >
                <div className="flex gap-4">
                  {/* Unread indicator */}
                  <div className="flex-shrink-0 pt-1.5">
                    {!notification.isRead ? (
                      <div className="w-2.5 h-2.5 bg-black" />
                    ) : (
                      <div className="w-2.5 h-2.5" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-100 px-1.5 py-0.5 border border-gray-300">
                        {notification.typeDisplay}
                      </span>
                      <span className="font-mono text-[10px] text-gray-400 ml-auto flex-shrink-0">
                        {formatDate(notification.createdAt)}
                      </span>
                    </div>
                    <p className={`font-mono text-sm text-black ${!notification.isRead ? 'font-bold' : 'font-medium'}`}>
                      {notification.title}
                    </p>
                    {notification.message && (
                      <p className="font-mono text-xs text-gray-500 mt-1">
                        {notification.message}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Load More */}
        {hasMore && (
          <div className="px-6 py-6 text-center">
            <Button
              variant="secondary"
              onClick={handleLoadMore}
              disabled={isLoading}
            >
              {isLoading ? 'LOADING...' : 'LOAD MORE'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
